import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { safeLocalStorage } from '../utils/safeLocalStorage';

const NAMESPACE = 'filters';

function buildKey(userId: string, pageKey: string) {
  return `${NAMESPACE}:${userId}:${pageKey}`;
}

function useDebouncedEffect(effect: () => void, deps: any[], delay: number) {
  useEffect(() => {
    const id = setTimeout(effect, delay);
    return () => clearTimeout(id);
  }, [...deps, delay]);
}

export type CacheOptions<T> = {
  // Required: distinct per page (e.g., 'Portal Landing page', 'Broker Management Landing page')
  pageKey: string;
  // Required: logged-in user id or email
  userId: string;
  // Default value when storage is empty
  defaultFilters: T;
  // Debounce ms for storage writes
  debounceMs?: number;
};

export function useCacheFilters<T>(opts: CacheOptions<T>) {
  const {
    pageKey,
    userId,
    defaultFilters,
    debounceMs = 500,
  } = opts;

  const storageKey = useMemo(
    () => buildKey(userId, pageKey),
    [userId, pageKey],
  );

  const [filters, setFilters] = useState<T>(() => safeLocalStorage.get<T>(storageKey, defaultFilters));
  const prevFiltersRef = useRef<T>(filters);

  // Debounced persist to localStorage
  useDebouncedEffect(
    () => {
      if (JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters)) {
        safeLocalStorage.set(storageKey, filters);
        prevFiltersRef.current = filters;
      }
    },
    [filters, storageKey],
    debounceMs,
  );

  const updateFilters = useCallback(
    (updater: Partial<T> | ((prev: T) => T)) => {
      setFilters((prev) => (typeof updater === 'function'
        ? (updater as (p: T) => T)(prev)
        : ({ ...prev, ...updater } as T)));
    },
    [],
  );

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
    safeLocalStorage.set(storageKey, defaultFilters);
  }, [defaultFilters, storageKey]);

  const clearStorage = useCallback(() => {
    safeLocalStorage.remove(storageKey);
  }, [storageKey]);

  return {
    filters,
    setFilters,
    updateFilters,
    resetFilters,
    clearStorage,
    storageKey,
  };
}
